import React, { useMemo } from 'react';
import { View, Switch, StyleSheet, ScrollView, ActivityIndicator } from 'react-native';
import { Text } from '@/components/common/text';
import { useAccountColors } from '@/constants/residents/mobile/accountColors';
import { useNotificationColors } from '@/constants/residents/mobile/notificationColors';
import { strings } from '@/constants/residents/strings';
import { useNotificationPreferences } from '@/hooks/residents/useNotificationPreferences';
import { NOTIFICATION_TYPES } from '@/constants/residents/notifications';

// Notification setting configuration from centralized strings
interface NotificationSetting {
  key: string;
  label: string;
  description: string;
}

const NOTIFICATION_SETTINGS: NotificationSetting[] = [
  {
    key: NOTIFICATION_TYPES.rentReminders.key,
    label: strings.notifications.settings.rentReminders.label,
    description: strings.notifications.settings.rentReminders.description,
  },
  {
    key: NOTIFICATION_TYPES.maintenanceUpdates.key,
    label: strings.notifications.settings.maintenanceUpdates.label,
    description: strings.notifications.settings.maintenanceUpdates.description,
  },
  {
    key: NOTIFICATION_TYPES.communityUpdates.key,
    label: strings.notifications.settings.communityUpdates.label,
    description: strings.notifications.settings.communityUpdates.description,
  },
];

/**
 * Mobile Notification Settings Section
 * Allows users to configure their notification preferences
 * Based on Figma design node 11026:295146
 */
export const MobileNotificationSettingsSection: React.FC = () => {
  const ac = useAccountColors();
  const nc = useNotificationColors();
  const styles = useMemo(() => makeStyles(ac), [ac]);
  const { getValue, togglePreference, isFetching, isUpdating } = useNotificationPreferences();

  const isLoading = isFetching || isUpdating;

  const handleToggle = (key: string, value: boolean) => {
    togglePreference(key, value, 'push');
  };

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.headerTitle}>{strings.notifications.settings.title}</Text>
      </View>

      {/* Description */}
      <View style={styles.descriptionContainer}>
        <Text style={styles.descriptionText}>
          {strings.notifications.settings.description}
        </Text>
      </View>

      {/* Notification Settings List */}
      <ScrollView 
        style={styles.scrollView}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
      >
        {isLoading && !NOTIFICATION_SETTINGS.length ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={ac.buttonPrimary} />
          </View>
        ) : (
          NOTIFICATION_SETTINGS.map((setting, index) => (
            <View
              key={setting.key}
              style={[
                styles.settingRow,
                index < NOTIFICATION_SETTINGS.length - 1 && styles.settingRowBorder,
              ]}
            >
              <View style={styles.settingContent}>
                <View style={styles.settingTextContainer}>
                  <Text style={styles.settingLabel}>{setting.label}</Text>
                  <Text style={styles.settingDescription}>{setting.description}</Text>
                </View>
                <Switch
                  value={getValue(setting.key)}
                  onValueChange={(value) => handleToggle(setting.key, value)}
                  trackColor={{
                    false: nc.switchTrackOff,
                    true: nc.switchTrackOn,
                  }}
                  thumbColor={nc.switchThumb}
                  ios_backgroundColor={nc.switchTrackOff}
                  disabled={isLoading}
                />
              </View>
            </View>
          ))
        )}
      </ScrollView>
    </View>
  );
};

const makeStyles = (ac: ReturnType<typeof useAccountColors>) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: ac.contentBackground,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: ac.innerBorder,
    overflow: 'hidden',
  },
  header: {
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: ac.divider,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: ac.textPrimary,
    lineHeight: 27,
  },
  descriptionContainer: {
    paddingHorizontal: 20,
    paddingVertical: 24,
    borderBottomWidth: 1,
    borderBottomColor: ac.divider,
  },
  descriptionText: {
    fontSize: 16,
    fontWeight: '400',
    color: ac.textPrimary,
    lineHeight: 24,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 40,
  },
  settingRow: {
    paddingHorizontal: 20,
    paddingVertical: 20,
  },
  settingRowBorder: {
    borderBottomWidth: 1,
    borderBottomColor: ac.divider,
  },
  settingContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
  },
  settingTextContainer: {
    flex: 1,
    gap: 4,
  },
  settingLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: ac.textPrimary,
    lineHeight: 21,
  },
  settingDescription: {
    fontSize: 12,
    fontWeight: '300',
    color: ac.textSecondary,
    lineHeight: 18,
  },
});

export default MobileNotificationSettingsSection;
